import { useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem, TextField, Autocomplete,
    FormControl, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper, Checkbox, FormControlLabel, Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import PercentInput from '../PercentInput';
import countries from '../Countries_Data';
import nationalityToCountry from '../../utils/nationalityToCountry';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, BLANK_QUOTE_RATES, BlankQuoteNotice, applyLeaveBlank, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

const INSURER_ID = 'cigna_global_health_option';
const BRAND_COLOR = '#00629B';
const TIER_ROW_COLOR = '#b9f6ca';
const TOTAL_ROW_COLOR = '#dfe3ec';

// Tiers are complete, mutually exclusive plans — same order as CignaGlobalHealthOption_Rates.
// International Inpatient is bundled into every tier and can't be unchecked; the rest are
// independently selectable riders whose field names lead with the tier rather than the
// module - matches the rate provider's response field naming.
const TIERS = [
    { key: 'silver', label: 'Silver' },
    { key: 'gold', label: 'Gold' },
    { key: 'platinum', label: 'Platinum' },
];

const MANDATORY_CATEGORY_KEY = 'ip';

const CATEGORIES = [
    { key: 'ip', label: 'International Inpatient', premiumField: 'ip_premium', field: (tier) => `ip_${tier}` },
    { key: 'op', label: 'International Outpatient', premiumField: 'op_premium', field: (tier) => `op_${tier}` },
    { key: 'medical_evacuation', label: 'International Medical Evacuation', premiumField: 'medical_evacuation_premium', field: (tier) => `${tier}_medical_evacuation` },
    { key: 'health_and_wellbeing', label: 'International Health & Wellbeing', premiumField: 'health_and_wellbeing_premium', field: (tier) => `${tier}_health_and_wellbeing` },
    { key: 'vision_and_dental', label: 'International Vision & Dental', premiumField: 'vision_and_dental_premium', field: (tier) => `${tier}_vision_and_dental` },
];

const RIDERS = CATEGORIES.filter(({ key }) => key !== MANDATORY_CATEGORY_KEY);

// Every tier × category field the API returns — all are kept so switching tier needs no refetch
const RATE_FIELDS = TIERS.flatMap(t => CATEGORIES.map(c => c.field(t.key)));

const AREA_OF_COVERAGE_WITH_USA = 'Worldwide incl USA';
const AREA_OF_COVERAGE_WITHOUT_USA = 'Worldwide excl USA';

// The rate provider only looks up a contracting country premium tax override for these
// residences - every other residence just uses its own country's tax rate.
const CONTRACTING_COUNTRY_RESIDENCES = ['Australia', 'Canada', 'United States of America'];

// Same total row label as CignaGlobalHealthOption_Rates' getPremiumLabel
const PREMIUM_LABELS = {
    Annually: 'Annual Premium',
    Monthly: 'Monthly Premium',
    Quarterly: 'Quarterly Premium',
};

function getCurrencySign(currency) {
    if (currency === 'USD') return '$';
    if (currency === 'SGD') return 'S$';
    if (currency === 'EURO') return '€';
    if (currency === 'GBP') return '£';
    return '';
}

function formatRate(rate, currency) {
    if (rate === null || rate === undefined) return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return getCurrencySign(currency) + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Same as CignaGlobalHealthOption_Rates: discount/loading scale every benefit
function getRateFactor(discount, loading) {
    return (100 - (parseFloat(discount) || 0) + (parseFloat(loading) || 0)) / 100;
}

export default function CignaGlobalHealthOption_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const config = appConfig?.content?.specifics?.[INSURER_ID] || {};
    const inpatientCostShareByCurrency = config['Inpatient Cost Share'] || {};
    const outpatientDeductibleByCurrency = config['Outpatient Deductible'] || {};
    const outpatientCostShareByCurrency = config['Outpatient Cost Share'] || {};
    const paymentFrequencyRate = config['Payment Frequency Rate'] || {};

    const deductiblesByCurrency = appConfig?.content?.deductibles?.[INSURER_ID] || {};

    const currencyOptions = Object.keys(deductiblesByCurrency).length > 0 ? Object.keys(deductiblesByCurrency) : ['USD', 'EURO', 'GBP'];
    const paymentFrequencyOptions = Object.keys(paymentFrequencyRate).length > 0 ? Object.keys(paymentFrequencyRate) : ['Monthly', 'Quarterly', 'Annually'];
    const defaultArea = AREA_OF_COVERAGE_WITHOUT_USA;

    const [tier, setTier] = useState('silver');
    const [selectedRiders, setSelectedRiders] = useState(new Set());
    const [areaOfCoverage, setAreaOfCoverage] = useState(defaultArea);
    const [currency, setCurrency] = useState('USD');
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [contractingCountry, setContractingCountry] = useState('');
    const [discount, setDiscount] = useState('0');
    const [loadingPercent, setLoadingPercent] = useState('0');
    // { [quoteName]: { deductible, inpatient_cost_share, outpatient_deductible, outpatient_cost_share } }
    const [quoteDeductibles, setQuoteDeductibles] = useState({});
    // { [quoteName]: { deductible: true, inpatient_cost_share: true, outpatient_deductible: true, outpatient_cost_share: true } }
    const [deductibleErrors, setDeductibleErrors] = useState({});
    const [ratesData, setRatesData] = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    const [loading, setLoading] = useState(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    const selectedTierObj = TIERS.find(t => t.key === tier) || TIERS[0];
    const activeRiders = RIDERS.filter(r => selectedRiders.has(r.key));
    const selectedCategories = [CATEGORIES.find(c => c.key === MANDATORY_CATEGORY_KEY), ...activeRiders];

    function getDefaultQuoteDeductibles(curr) {
        const defaults = {};
        (quotes || []).forEach(q => {
            defaults[q.name] = {
                deductible: deductiblesByCurrency[curr]?.[0] || '',
                inpatient_cost_share: inpatientCostShareByCurrency[curr]?.[0] || '',
                outpatient_deductible: outpatientDeductibleByCurrency[curr]?.[0] || '',
                outpatient_cost_share: outpatientCostShareByCurrency[curr]?.[0] || '',
            };
        });
        return defaults;
    }

    // Pre-populate form when opening
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            const savedTier = editingPlan.plan || (editingPlan.plan_id || '').replace(`${INSURER_ID}_`, '') || 'silver';
            setTier(savedTier);
            setSelectedRiders(new Set(editingPlan.payloads?.riders || []));
            setAreaOfCoverage(editingPlan.payloads?.area_of_coverage || defaultArea);
            setCurrency(editingPlan.payloads?.currency || 'USD');
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            setContractingCountry(editingPlan.payloads?.contracting_country || '');
            const savedDiscount = editingPlan.payloads?.discount ?? '0';
            const savedLoading = editingPlan.payloads?.loading ?? '0';
            setDiscount(String(savedDiscount));
            setLoadingPercent(String(savedLoading));
            const savedFactor = getRateFactor(savedDiscount, savedLoading);
            // Undo the saved discount/loading so ratesData holds the raw rate again
            const unscale = val => (typeof val === 'number' && savedFactor > 0 ? val / savedFactor : val);

            const deductibles = {};
            (quotes || []).forEach(quote => {
                deductibles[quote.name] = editingPlan.deductibles_per_quote?.[quote.uid] || {};
            });
            setQuoteDeductibles(deductibles);
            setDeductibleErrors({});

            // Reconstruct ratesData from stored per-category premiums — only the saved tier's
            // fields are recoverable
            const ratesMap = {};
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                const rawRates = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    // Prefer matching the specific plan instance (plan_uid) so editing one of several
                    // same-tier plans doesn't pull rates from a sibling; fall back to plan_id for plans
                    // saved before per-instance uids existed.
                    const premium = clientPremium?.premiums?.find(p =>
                        editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id
                    );
                    return Object.fromEntries(CATEGORIES.map(c => [c.field(savedTier), unscale(premium?.[c.premiumField] ?? null)]));
                });
                ratesMap[quote.uid] = { rawRates };
            });
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
        } else if (open && !editingPlan) {
            setTier('silver');
            setSelectedRiders(new Set());
            setAreaOfCoverage(defaultArea);
            setCurrency('USD');
            setPaymentFrequency('Annually');
            setContractingCountry('');
            setDiscount('0');
            setLoadingPercent('0');
            setQuoteDeductibles(getDefaultQuoteDeductibles('USD'));
            setDeductibleErrors({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    function handleCurrencyChange(newCurrency) {
        setCurrency(newCurrency);
        // Every deductible / cost share list depends on the currency — reset to the new
        // currency's defaults, keeping any "Leave Blank" quotes blank
        const defaults = getDefaultQuoteDeductibles(newCurrency);
        setQuoteDeductibles(prev => {
            const next = {};
            Object.keys(defaults).forEach(name => {
                next[name] = prev[name]?.deductible === LEAVE_BLANK
                    ? { ...defaults[name], deductible: LEAVE_BLANK }
                    : defaults[name];
            });
            return next;
        });
        setDeductibleErrors({});
        invalidateRates();
    }

    // Every benefit's rate comes back in one fetch, so toggling a rider just shows a different
    // set of rows. Outpatient's deductible / cost share are part of the request though, so if
    // ticking it has to fill those in, the shown rates no longer match.
    function toggleRider(key) {
        const adding = !selectedRiders.has(key);
        setSelectedRiders(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
        if (key !== 'op' || !adding) return;
        const needsDefaults = (quotes || []).some(q => !quoteDeductibles[q.name]?.outpatient_deductible || !quoteDeductibles[q.name]?.outpatient_cost_share);
        if (!needsDefaults) return;
        setQuoteDeductibles(prev => {
            const next = { ...prev };
            (quotes || []).forEach(q => {
                const existing = { ...(prev[q.name] || {}) };
                if (!existing.outpatient_deductible) existing.outpatient_deductible = outpatientDeductibleByCurrency[currency]?.[0] || '';
                if (!existing.outpatient_cost_share) existing.outpatient_cost_share = outpatientCostShareByCurrency[currency]?.[0] || '';
                next[q.name] = existing;
            });
            return next;
        });
        invalidateRates();
    }

    function setQuoteField(quoteName, field, value) {
        setQuoteDeductibles(prev => ({ ...prev, [quoteName]: { ...(prev[quoteName] || {}), [field]: value } }));
        setDeductibleErrors(prev => ({ ...prev, [quoteName]: { ...(prev[quoteName] || {}), [field]: false } }));
        invalidateRates();
    }

    function requiresContractingCountry() {
        return (clients || []).some(client => CONTRACTING_COUNTRY_RESIDENCES.includes(client.country_of_residence));
    }

    // e.g. a Filipino national residing in the Philippines - this combination can't be
    // rated and needs manual handling by sales support.
    function hasSameNationalityAndResidence() {
        return (clients || []).some(client => {
            const nationalityCountry = nationalityToCountry(client.nationality);
            return nationalityCountry && nationalityCountry === client.country_of_residence;
        });
    }

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return quoteDeductibles[quote.name]?.deductible === LEAVE_BLANK;
    }

    async function handleViewRates() {
        const errors = {};
        (quotes || []).forEach(q => {
            if (isBlankQuote(q)) return;
            const d = quoteDeductibles[q.name];
            const qErrors = {};
            if (!d?.deductible) qErrors.deductible = true;
            if (!d?.inpatient_cost_share) qErrors.inpatient_cost_share = true;
            if (selectedRiders.has('op')) {
                if (!d?.outpatient_deductible) qErrors.outpatient_deductible = true;
                if (!d?.outpatient_cost_share) qErrors.outpatient_cost_share = true;
            }
            if (Object.keys(qErrors).length > 0) errors[q.name] = qErrors;
        });
        if (Object.keys(errors).length > 0) {
            setDeductibleErrors(errors);
            const firstQuote = (quotes || []).find(q => errors[q.name]);
            toast.error(`Please complete deductibles for "${firstQuote?.name}"`, { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        if (requiresContractingCountry() && !contractingCountry) {
            toast.error('Please select a Contracting Country', { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        setDeductibleErrors({});
        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: { [INSURER_ID]: quoteDeductibles[quote.name]?.deductible || '' },
                            insurers: [INSURER_ID],
                            specifics: {
                                [INSURER_ID]: {
                                    inpatient_cost_share: quoteDeductibles[quote.name]?.inpatient_cost_share || '',
                                    outpatient_deductible: quoteDeductibles[quote.name]?.outpatient_deductible || '',
                                    outpatient_cost_share: quoteDeductibles[quote.name]?.outpatient_cost_share || '',
                                    contracting_country: contractingCountry || '',
                                },
                            },
                            specific_area_of_coverage: { [INSURER_ID]: areaOfCoverage },
                            rates_version_per_insurer: { [INSURER_ID]: selectedRatesVersion?.[INSURER_ID] || '' },
                            specific_currency: { [INSURER_ID]: currency },
                            specific_payment_frequency: { [INSURER_ID]: paymentFrequency },
                        },
                    }).then(response => ({ quoteUid: quote.uid, data: response?.data?.[INSURER_ID] }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quoteUid, data, blank }) => {
                if (blank) {
                    ratesMap[quoteUid] = BLANK_QUOTE_RATES;
                    return;
                }
                if (data?.rates) {
                    const rawRates = data.rates.map(r => Object.fromEntries(RATE_FIELDS.map(f => [f, r[f]])));
                    ratesMap[quoteUid] = { rawRates };
                } else {
                    ratesMap[quoteUid] = data;
                }
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching Cigna Global Health Option rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    const rateFactor = getRateFactor(discount, loadingPercent);

    // Benefit rate for the selected tier with discount/loading applied; ratesData keeps the raw API rates
    function getCategoryRate(clientIndex, category, quoteUid) {
        const val = ratesData?.[quoteUid]?.rawRates?.[clientIndex]?.[category.field(tier)];
        if (val === undefined || val === null) return null;
        return typeof val === 'number' ? val * rateFactor : val;
    }

    // Cumulative rate for the mandatory category plus every selected rider
    function getTotal(clientIndex, quoteUid) {
        let total = 0;
        let hasAny = false;
        selectedCategories.forEach(c => {
            const rate = getCategoryRate(clientIndex, c, quoteUid);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        });
        return hasAny ? total : null;
    }

    function sumClients(getter) {
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getter(i);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function buildDeductibleString(quoteName) {
        const d = quoteDeductibles[quoteName] || {};
        const parts = [];
        if (d.deductible) parts.push(d.deductible);
        if (d.inpatient_cost_share) parts.push(`Cost Share: ${d.inpatient_cost_share}`);
        if (selectedRiders.has('op')) {
            if (d.outpatient_deductible) parts.push(`OP Deductible: ${d.outpatient_deductible}`);
            if (d.outpatient_cost_share) parts.push(`OP Cost Share: ${d.outpatient_cost_share}`);
        }
        return parts.join(' | ');
    }

    function buildPlanLabel() {
        let label = selectedTierObj.label;
        if (activeRiders.length > 0) {
            label += ' + ' + activeRiders.map(r => r.label).join(' + ');
        }
        return label;
    }

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;

        const planLabel = buildPlanLabel();
        const planId = `${INSURER_ID}_${tier}`;
        // plan_id identifies the tier ("cigna_global_health_option_silver") and is shared by
        // every instance of that tier; uid identifies this specific plan instance so multiple
        // plans of the same tier (e.g. two Silvers) can coexist instead of overwriting each other.
        // Editing reuses the existing plan's uid so updates land on the same instance.
        const planUid = editingPlan ? editingPlan.uid : uuidv4();
        const updates = [];
        (quotes || []).forEach(quote => {
            clients.forEach((client, clientIndex) => {
                const rate = getTotal(clientIndex, quote.uid);

                // Stored with discount/loading applied, matching premium_rate
                const premium = {
                    insurer: INSURER_ID,
                    plan: tier,
                    plan_label: planLabel,
                    included_benefits: '',
                    deductible: buildDeductibleString(quote.name),
                    area_of_coverage: areaOfCoverage,
                    currency,
                    payment_frequency: paymentFrequency,
                    contracting_country: contractingCountry || '',
                    discount,
                    loading: loadingPercent,
                    premium_rate: rate ?? null,
                };

                CATEGORIES.forEach(c => {
                    const isActive = c.key === MANDATORY_CATEGORY_KEY || selectedRiders.has(c.key);
                    premium[c.premiumField] = isActive ? getCategoryRate(clientIndex, c, quote.uid) : null;
                });

                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId,
                    planUid,
                    premium,
                });
            });
        });

        const plan = {
            plan_id: planId,
            uid: planUid,
            plan: tier,
            insurer: INSURER_ID,
            label: `Cigna Global Health Option ${planLabel}`,
            payloads: {
                area_of_coverage: areaOfCoverage,
                currency,
                payment_frequency: paymentFrequency,
                contracting_country: contractingCountry || '',
                riders: [...selectedRiders],
                discount,
                loading: loadingPercent,
            },
            deductibles_per_quote: Object.fromEntries(
                (quotes || []).map(quote => [quote.uid, quoteDeductibles[quote.name] || {}])
            ),
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    const selectSx = { fontSize: 12, height: 32 };
    const menuItemSx = { fontSize: 12 };
    const checkboxLabelSx = { mb: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } };
    const inpatientCategory = CATEGORIES.find(c => c.key === MANDATORY_CATEGORY_KEY);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: BRAND_COLOR, px: 2, py: 1 }}>
                <div style={{
                    color: '#fff',
                    backgroundColor: BRAND_COLOR,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 18,
                    lineHeight: 1.2,
                    height: 60,
                }}>Cigna Global Health Option</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', background: '#f8f9fa', px: 1.5, py: 0.5 }}>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/cigna-logo.svg`}
                            alt="Cigna"
                            style={{ height: 55, userSelect: 'none', WebkitUserDrag: 'none' }}
                            draggable={false}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    </Box>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                {/* Plan Type */}
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={tier}
                            disabled={loading || !!editingPlan}
                            // Rates for every tier come back in one fetch, so switching tiers just shows different rows
                            onChange={(e) => setTier(e.target.value)}
                            sx={selectSx}
                        >
                            {TIERS.map(t => (
                                <MenuItem key={t.key} value={t.key} sx={menuItemSx}>{t.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Benefit — International Inpatient is always included */}
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                    <Typography fontWeight={600} sx={{ fontSize: 15 }}>Benefit</Typography>
                    <FormControlLabel
                        sx={checkboxLabelSx}
                        control={<Checkbox size="small" checked onChange={(e) => e.preventDefault()} />}
                        label={inpatientCategory.label}
                    />
                    {RIDERS.map(rider => (
                        <FormControlLabel
                            key={rider.key}
                            sx={checkboxLabelSx}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={selectedRiders.has(rider.key)}
                                    onChange={() => toggleRider(rider.key)}
                                    disabled={loading || !!editingPlan}
                                />
                            }
                            label={rider.label}
                        />
                    ))}
                </Box>

                {/* Options — label on the left, select on the right */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2, maxWidth: 400 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'center' }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Area of Coverage</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={areaOfCoverage} disabled={loading} onChange={(e) => { setAreaOfCoverage(e.target.value); invalidateRates(); }} sx={selectSx}>
                                {[AREA_OF_COVERAGE_WITHOUT_USA, AREA_OF_COVERAGE_WITH_USA].map(area => (
                                    <MenuItem key={area} value={area} sx={menuItemSx}>{area}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Currency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={currency} disabled={loading} onChange={(e) => handleCurrencyChange(e.target.value)} sx={selectSx}>
                                {currencyOptions.map(c => <MenuItem key={c} value={c} sx={menuItemSx}>{c}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={paymentFrequency} disabled={loading} onChange={(e) => { setPaymentFrequency(e.target.value); invalidateRates(); }} sx={selectSx}>
                                {paymentFrequencyOptions.map(f => <MenuItem key={f} value={f} sx={menuItemSx}>{f}</MenuItem>)}
                            </Select>
                        </FormControl>

                        {requiresContractingCountry() && (
                            <>
                                <Typography fontWeight={600} sx={{ fontSize: 13 }}>Contracting Country</Typography>
                                <Autocomplete
                                    disablePortal
                                    disableClearable
                                    options={countries}
                                    sx={{
                                        width: 170,
                                        '& .MuiAutocomplete-input': { fontSize: 12 },
                                        '& .MuiOutlinedInput-root': { height: 32, py: '0 !important' },
                                    }}
                                    size="small"
                                    value={contractingCountry || null}
                                    onChange={(event, newValue) => { setContractingCountry(newValue || ''); invalidateRates(); }}
                                    disabled={loading}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </>
                        )}
                    </Box>

                    {requiresContractingCountry() && (
                        <Typography fontSize={11} sx={{ color: '#1976d2' }}>
                            Contracting Country is required for Australia, Canada, and United States of America Country of Residence
                        </Typography>
                    )}

                    {hasSameNationalityAndResidence() && (
                        <Typography fontSize={11} sx={{ color: '#f44336' }}>
                            Clients have the same Nationality and Country of Residence. To proceed with this quote, we need additional information about the nationality and country of residence of the policyholder. Please contact our sales support team on 971-4561-3804 or 1-877-539-6296.
                        </Typography>
                    )}
                </Box>

                {/* Renewal/Further Discount & Loading */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Discount</Typography>
                        <PercentInput
                            value={discount}
                            onCommit={setDiscount}
                            disabled={loading}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Loading</Typography>
                        <PercentInput
                            value={loadingPercent}
                            onCommit={setLoadingPercent}
                            disabled={loading}
                        />
                </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Per-Quote Deductibles — Inpatient deductible / cost share, plus Outpatient's when ticked */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>Deductibles per Quote</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {(quotes || []).map(quote => (
                            <Box key={quote.name}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>{quote.name}</Typography>
                                <Box sx={{ display: 'flex', columnGap: 4, rowGap: 1.5, pl: 1, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 190px', columnGap: 2, rowGap: 1, alignItems: 'center', alignContent: 'start' }}>
                                        <Typography sx={{ gridColumn: '1 / -1', fontSize: 12, color: 'text.secondary' }}>{inpatientCategory.label}</Typography>
                                        <Typography fontWeight={600} sx={{ fontSize: 12 }}>Core Deductible</Typography>
                                        <CustomSelect2
                                            dense
                                            value={quoteDeductibles[quote.name]?.deductible || ''}
                                            onChange={(e) => setQuoteField(quote.name, 'deductible', e.target.value)}
                                            items={[LEAVE_BLANK, ...(deductiblesByCurrency[currency] || [])]}
                                            disabled={loading}
                                            error={!!deductibleErrors[quote.name]?.deductible}
                                            sx={{ minWidth: 190 }}
                                        />

                                        {!isBlankQuote(quote) && (
                                            <>
                                                <Typography fontWeight={600} sx={{ fontSize: 12 }}>Cost Share after Deductible</Typography>
                                                <CustomSelect2
                                                    dense
                                                    value={quoteDeductibles[quote.name]?.inpatient_cost_share || ''}
                                                    onChange={(e) => setQuoteField(quote.name, 'inpatient_cost_share', e.target.value)}
                                                    items={inpatientCostShareByCurrency[currency] || []}
                                                    disabled={loading}
                                                    error={!!deductibleErrors[quote.name]?.inpatient_cost_share}
                                                    isZeroDeductibleUniform={false}
                                                    sx={{ minWidth: 190 }}
                                                />
                                            </>
                                        )}
                                    </Box>

                                    {selectedRiders.has('op') && !isBlankQuote(quote) && (
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 190px', columnGap: 2, rowGap: 1, alignItems: 'center', alignContent: 'start' }}>
                                            <Typography sx={{ gridColumn: '1 / -1', fontSize: 12, color: 'text.secondary' }}>International Outpatient</Typography>
                                            <Typography fontWeight={600} sx={{ fontSize: 12 }}>OP Deductible</Typography>
                                            <CustomSelect2
                                                dense
                                                value={quoteDeductibles[quote.name]?.outpatient_deductible || ''}
                                                onChange={(e) => setQuoteField(quote.name, 'outpatient_deductible', e.target.value)}
                                                items={outpatientDeductibleByCurrency[currency] || []}
                                                disabled={loading}
                                                error={!!deductibleErrors[quote.name]?.outpatient_deductible}
                                                sx={{ minWidth: 190 }}
                                            />

                                            <Typography fontWeight={600} sx={{ fontSize: 12 }}>OP Cost Share</Typography>
                                            <CustomSelect2
                                                dense
                                                value={quoteDeductibles[quote.name]?.outpatient_cost_share || ''}
                                                onChange={(e) => setQuoteField(quote.name, 'outpatient_cost_share', e.target.value)}
                                                items={outpatientCostShareByCurrency[currency] || []}
                                                disabled={loading}
                                                error={!!deductibleErrors[quote.name]?.outpatient_cost_share}
                                                isZeroDeductibleUniform={false}
                                                sx={{ minWidth: 190 }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: BRAND_COLOR }} />
                    </Box>
                )}

                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ mb: 3 }}>
                                <Typography fontWeight={600} sx={{ mb: 1 }}>
                                    {quote.name}
                                    <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 13 }}>
                                        — {isBlankQuote(quote) ? LEAVE_BLANK : (buildDeductibleString(quote.name) || '-')}
                                    </Typography>
                                </Typography>
                                {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: BRAND_COLOR }}>
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 260 }}>Plan</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                        <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                        <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {/* Tier row carries the International Inpatient rate — same as CignaGlobalHealthOption_Rates */}
                                            <TableRow sx={{ backgroundColor: TIER_ROW_COLOR }}>
                                                <TableCell sx={{ fontWeight: 600, color: '#000' }}>{selectedTierObj.label}</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx}>
                                                        {formatRate(getCategoryRate(idx, inpatientCategory, quote.uid), currency)}
                                                    </TableCell>
                                                ))}
                                                <TableCell>
                                                    {formatRate(sumClients(i => getCategoryRate(i, inpatientCategory, quote.uid)), currency)}
                                                </TableCell>
                                            </TableRow>
                                            {activeRiders.map((rider, riderIdx) => (
                                                <TableRow key={rider.key} sx={{ backgroundColor: riderIdx % 2 === 0 ? '#f5f7fa' : '#fff' }}>
                                                    <TableCell sx={{ color: 'text.secondary' }}>{rider.label}</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            {formatRate(getCategoryRate(idx, rider, quote.uid), currency)}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        {formatRate(sumClients(i => getCategoryRate(i, rider, quote.uid)), currency)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow sx={{ backgroundColor: TOTAL_ROW_COLOR }}>
                                                <TableCell sx={{ fontWeight: 700 }}>{PREMIUM_LABELS[paymentFrequency] || 'Total Premium'}</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ fontWeight: 700 }}>
                                                        {formatRate(getTotal(idx, quote.uid), currency)}
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ fontWeight: 700 }}>
                                                    {formatRate(sumClients(i => getTotal(i, quote.uid)), currency)}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Paper>
)}
                            </Box>
                        ))}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId={INSURER_ID} />

                <RatesVersionSelect
                    insurer={INSURER_ID}
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />

                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
